import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useId, useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Input } from '../../atoms/Input/Input.js';

// ─── Icons ────────────────────────────────────────────────────────────────────
const iconProps = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
};
const CalendarIcon = () => (jsxs("svg", { ...iconProps, children: [jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" }), jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" }), jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" })] }));
const ChevronLeft = () => (jsx("svg", { ...iconProps, children: jsx("polyline", { points: "15 18 9 12 15 6" }) }));
const ChevronRight = () => (jsx("svg", { ...iconProps, children: jsx("polyline", { points: "9 18 15 12 9 6" }) }));
const ChevronDown = ({ className }) => (jsx("svg", { ...iconProps, width: 12, height: 12, className: className, children: jsx("polyline", { points: "6 9 12 15 18 9" }) }));
// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
    baseButton: 'cursor-pointer mx-auto text-body transition-colors relative flex items-center justify-center border focus:border-border-focus-2 focus:outline-1 focus:outline-border-focus-1 hover:bg-button-bg-outline-hover hover:text-button-text-outline-hover',
    roundButton: 'w-9 h-9 tablet:w-12 tablet:h-12 rounded-full',
    squareButton: 'w-17 h-10 rounded-radius-200',
    selected: 'bg-button-bg-outline-active text-button-text-outline-active',
    today: 'border-button-border-outline-default',
    transparent: 'border-transparent',
    disabled: 'text-button-text-outline-disabled pointer-events-none opacity-50',
    popoverContent: 'p-space-300 w-full min-w-0 tablet:min-w-105',
    topRow: 'flex items-center justify-between mb-space-400',
    monthYearGrid: 'grid grid-cols-3 tablet:grid-cols-4 gap-space-100',
    daysGrid: 'grid grid-cols-7 gap-1',
    dayLetter: 'mx-auto flex w-9 h-9 tablet:w-12 tablet:h-12 text-body-strong justify-center items-center',
    typeSelector: 'cursor-pointer flex items-center gap-space-100 text-body hover:bg-bg-main px-2 py-1 rounded-radius-100 transition-colors',
    navContainer: 'flex items-center gap-space-100',
    navButton: 'cursor-pointer h-7 w-7 p-0 flex items-center justify-center rounded-radius-100 hover:bg-bg-main transition-colors',
    empty: 'w-9 h-9 tablet:w-12 tablet:h-12',
    calendarButton: 'cursor-pointer flex items-center justify-center',
    popover: 'absolute top-full left-0 tablet:left-auto tablet:right-0 mt-2 w-full tablet:w-auto tablet:mx-0 bg-bg-main-container shadow-button rounded-radius-200 z-50 transition-transform duration-[220ms] ease-out tablet:max-w-none origin-top-left tablet:origin-top-right',
};
// ─── Date helpers ─────────────────────────────────────────────────────────────
/** Normalise a Date to midnight so all range comparisons ignore time-of-day. */
function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
/**
 * Returns true when at least one day in the given month falls inside the
 * min/max range. Used to enable/disable month buttons in the month-picker view.
 */
function isMonthInRange(year, month, minDate, maxDate) {
    const lastDay = new Date(year, month + 1, 0);
    const firstDay = new Date(year, month, 1);
    if (minDate && lastDay < startOfDay(minDate))
        return false;
    if (maxDate && firstDay > startOfDay(maxDate))
        return false;
    return true;
}
function getDateFormat(locale) {
    if (locale.startsWith('en-US')) {
        return {
            fields: ['month', 'day', 'year'],
            maxLengths: [2, 2, 4],
            placeholder: 'MM/DD/YYYY',
            separator: '/',
            maxLength: 10,
        };
    }
    return {
        fields: ['day', 'month', 'year'],
        maxLengths: [2, 2, 4],
        placeholder: 'DD/MM/YYYY',
        separator: '/',
        maxLength: 10,
    };
}
// ─── Input helpers ────────────────────────────────────────────────────────────
//
// These utilities handle the text input's auto-formatting behaviour:
//
// 1. `tokenise` splits a raw string like "12/26/2026" into digit groups and
//    the separators between them.
//
// 2. `autoFormat` inserts separators automatically as the user types. When a
//    digit group exceeds its max width (e.g. 2 digits for month), the overflow
//    spills into the next group and a separator is injected. It also handles
//    pasted values by detecting when all required digits are present and
//    rebuilding the formatted string in one pass.
//
// 3. Both functions track cursor position so the caret stays in the right
//    place after React re-renders the controlled input.
const SEPARATOR_RE = /[/\-.]/;
const SEPARATOR_CHARS = new Set(['/', '-', '.']);
function tokenise(input) {
    const groups = [];
    const separators = [];
    let current = '';
    for (const ch of input) {
        if (SEPARATOR_RE.test(ch)) {
            groups.push(current);
            separators.push(ch);
            current = '';
        }
        else if (/\d/.test(ch)) {
            current += ch;
        }
    }
    groups.push(current);
    return { groups, separators };
}
function autoFormat(value, cursorPos, format) {
    const totalDigits = format.maxLengths.reduce((sum, n) => sum + n, 0);
    const rawDigits = value.replace(/\D/g, '');
    // Fast path for pasted values or when all required digits are present:
    // rebuild the entire string with correct separators in one pass.
    if (rawDigits.length >= totalDigits) {
        const digits = rawDigits.slice(0, totalDigits);
        // Work out which digit the cursor sits after in the original input,
        // so we can map it to the equivalent position in the rebuilt string.
        let digitsBefore = 0;
        for (let i = 0; i < Math.min(cursorPos, value.length); i++) {
            if (/\d/.test(value[i]))
                digitsBefore++;
        }
        let rebuilt = '';
        let digitIndex = 0;
        for (let i = 0; i < format.maxLengths.length; i++) {
            const maxLen = format.maxLengths[i];
            const chunk = digits.slice(digitIndex, digitIndex + maxLen);
            digitIndex += maxLen;
            rebuilt += chunk;
            const isLastField = i === format.maxLengths.length - 1;
            if (!isLastField && chunk.length === maxLen) {
                rebuilt += format.separator;
            }
        }
        // Map digitsBefore back to a position in the rebuilt string by
        // walking through it and counting digits.
        let newCursor = 0;
        let counted = 0;
        for (let i = 0; i < rebuilt.length && counted < digitsBefore; i++) {
            newCursor = i + 1;
            if (/\d/.test(rebuilt[i]))
                counted++;
        }
        return { value: rebuilt, cursorPos: Math.min(newCursor, rebuilt.length) };
    }
    // Character-by-character path: keep user-typed separators and auto-insert
    // new ones only when a digit group overflows its max length.
    if (value.length > format.maxLength) {
        value = value.slice(0, format.maxLength);
        cursorPos = Math.min(cursorPos, format.maxLength);
    }
    const { groups, separators } = tokenise(value);
    let rebuilt = '';
    let newCursor = cursorPos;
    for (let i = 0; i < groups.length; i++) {
        let group = groups[i];
        const maxLen = format.maxLengths[i];
        if (maxLen && i < format.maxLengths.length - 1 && group.length > maxLen) {
            const overflow = group.slice(maxLen);
            group = group.slice(0, maxLen);
            const insertPos = rebuilt.length + group.length;
            rebuilt += group + format.separator;
            if (cursorPos > insertPos)
                newCursor += 1;
            if (i + 1 < groups.length)
                groups[i + 1] = overflow + groups[i + 1];
            else
                rebuilt += overflow;
            continue;
        }
        rebuilt += group;
        if (i < separators.length)
            rebuilt += separators[i];
    }
    if (rebuilt.length > format.maxLength) {
        rebuilt = rebuilt.slice(0, format.maxLength);
        newCursor = Math.min(newCursor, format.maxLength);
    }
    return { value: rebuilt, cursorPos: newCursor };
}
function isInputFull(input, format) {
    const totalDigits = format.maxLengths.reduce((sum, len) => sum + len, 0);
    return input.replace(/\D/g, '').length >= totalDigits;
}
/**
 * Parse a formatted date string back into a Date.
 *
 * In strict mode (default) every field must be fully zero-padded and the year
 * must be 4 digits. Lenient mode accepts shorthand like "2/2/26" — used for
 * blur-time parsing so users don't have to type leading zeros.
 */
function parseInputDate(input, format, lenient = false) {
    const { groups } = tokenise(input);
    if (groups.length < 3)
        return null;
    const values = { month: '', day: '', year: '' };
    for (let i = 0; i < 3; i++)
        values[format.fields[i]] = groups[i];
    const { month: monthStr, day: dayStr, year: yearStr } = values;
    if (!monthStr || !dayStr || !yearStr)
        return null;
    if (lenient) {
        if (monthStr.length < 1 || monthStr.length > 2)
            return null;
        if (dayStr.length < 1 || dayStr.length > 2)
            return null;
        if (yearStr.length !== 2 && yearStr.length !== 4)
            return null;
    }
    else {
        if (monthStr.length !== 2 || dayStr.length !== 2 || yearStr.length !== 4)
            return null;
    }
    const month = Number.parseInt(monthStr, 10);
    const day = Number.parseInt(dayStr, 10);
    let year = Number.parseInt(yearStr, 10);
    if (Number.isNaN(month) || Number.isNaN(day) || Number.isNaN(year))
        return null;
    if (yearStr.length === 2)
        year += 2000;
    // Round-trip through the Date constructor to catch impossible dates like Feb 30
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day)
        return null;
    return date;
}
/**
 * Validate the text input and return an error message (or empty string if valid).
 *
 * Validation is intentionally deferred while the user is still typing:
 * - In strict mode, incomplete input (fewer digits than expected) returns no error.
 * - In lenient mode, input with fewer than 3 groups is silently accepted.
 *
 * This prevents premature "invalid date" messages mid-keystroke.
 */
function validateInput(input, format, minDate, maxDate, lenient = false, errors) {
    if (input.trim() === '')
        return '';
    const digits = input.replace(/\D/g, '');
    const totalDigits = format.maxLengths.reduce((sum, len) => sum + len, 0);
    if (!lenient && digits.length < totalDigits)
        return '';
    const { groups } = tokenise(input);
    if (lenient && (groups.length < 3 || !groups[2]))
        return '';
    const parsed = parseInputDate(input, format, lenient);
    if (!parsed)
        return errors?.invalidDate || `Please enter a valid date (${format.placeholder})`;
    const parsedDay = startOfDay(parsed);
    if (minDate && parsedDay < startOfDay(minDate))
        return errors?.minDate || 'Date is before the earliest allowed date';
    if (maxDate && parsedDay > startOfDay(maxDate))
        return errors?.maxDate || 'Date is after the latest allowed date';
    return '';
}
function overwriteDigit(input, digit, format) {
    const { value: currentValue, selectionStart, selectionEnd } = input;
    if (!isInputFull(currentValue, format))
        return null;
    if (selectionStart !== selectionEnd || selectionStart === currentValue.length)
        return null;
    let targetPos = selectionStart ?? 0;
    if (targetPos < currentValue.length && SEPARATOR_CHARS.has(currentValue[targetPos]))
        targetPos++;
    if (targetPos >= currentValue.length)
        return null;
    const newValue = currentValue.slice(0, targetPos) + digit + currentValue.slice(targetPos + 1);
    let cursorPos = targetPos + 1;
    if (cursorPos < newValue.length && SEPARATOR_CHARS.has(newValue[cursorPos]))
        cursorPos++;
    return { value: newValue, cursorPos };
}
// ─── Component ────────────────────────────────────────────────────────────────
//
// DatePicker is a controlled component that pairs a formatted text input with a
// calendar popover. Users can enter dates by typing (with auto-formatting) or
// by clicking a day in the popover. The popover has three drill-down views:
// days → months → years, navigated via the top-row label button.
//
// Key behaviours:
//   - Typing auto-inserts separators and commits the value when all digits are entered.
//   - On blur, lenient parsing accepts shorthand like "2/2/26" and reformats it.
//   - Errors are suppressed until the user has blurred or completed the input.
//   - The popover closes on Escape, Enter, click-outside, or valid date entry.
//   - Multiple instances on the same page are safe (ids are generated via useId).
function DatePicker({ id, value, onChange, minDate, maxDate, locale = 'en-US', label, error, errors, }) {
    const format = getDateFormat(locale);
    const instanceId = useId();
    const popoverId = `datepicker-popover-${instanceId}`;
    const [open, setOpen] = useState(false);
    const openRef = useRef(false);
    const [mounted, setMounted] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [hasBlurred, setHasBlurred] = useState(false);
    // `viewDate` tracks which month/year the popover is displaying, independent
    // of the selected `value`. It follows partial input so the calendar stays
    // roughly in sync as the user types.
    const [viewDate, setViewDate] = useState(value || new Date());
    const [viewMode, setViewMode] = useState('days');
    const inputRef = useRef(null);
    const popoverRef = useRef(null);
    const toggleButtonRef = useRef(null);
    // We can't set cursor position synchronously during a React state update, so
    // we stash the desired position here and apply it in an effect. We also store
    // the input value that the cursor position corresponds to, so we can keep
    // reapplying across cascading re-renders (e.g. from syncFromInput triggering
    // onChange → parent re-render → value sync effect) until the value changes
    // from a new user interaction.
    const pendingCursorPos = useRef(null);
    // Track the close-animation timer so we can cancel it if the popover is
    // reopened before the animation finishes.
    const unmountTimerRef = useRef(null);
    const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(locale, { weekday: 'narrow' }).format(new Date(2000, 0, i + 2))), [locale]);
    const monthNames = useMemo(() => Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleDateString(locale, { month: 'short' })), [locale]);
    // Apply the stashed cursor position after every render so the caret doesn't
    // jump to the end when React replaces the input value. We keep reapplying as
    // long as the current input value matches the value the cursor was set for —
    // this survives cascading re-renders from syncFromInput → onChange → parent
    // re-render → value sync effect without the cursor snapping to the end.
    useEffect(() => {
        if (!pendingCursorPos.current || !inputRef.current)
            return;
        const { pos, forValue } = pendingCursorPos.current;
        if (inputRef.current.value === forValue) {
            inputRef.current.setSelectionRange(pos, pos);
        }
        else {
            // Value changed from a different source (new user keystroke, external
            // prop change), so this cursor position is stale.
            pendingCursorPos.current = null;
        }
    });
    // ── Popover lifecycle ─────────────────────────────────────────────────────
    //
    // Opening is two-phase: mount the DOM first (`mounted`), then on the next
    // frame set `open` to trigger the CSS scale transition. Closing reverses
    // this with a 220ms delay to let the animation finish before unmounting.
    // `openRef` mirrors `open` as a ref so event handlers that close on
    // state-change boundaries (e.g. syncFromInput) don't read stale state.
    const openPopover = useCallback(() => {
        // Cancel any pending unmount from a previous close — the user reopened
        // during the closing animation.
        if (unmountTimerRef.current) {
            clearTimeout(unmountTimerRef.current);
            unmountTimerRef.current = null;
        }
        openRef.current = true;
        setMounted(true);
        setTimeout(() => setOpen(true), 10);
    }, []);
    const closePopover = useCallback(() => {
        openRef.current = false;
        setOpen(false);
        unmountTimerRef.current = setTimeout(() => {
            setMounted(false);
            unmountTimerRef.current = null;
        }, 220);
    }, []);
    // Clean up the unmount timer on component tear down.
    useEffect(() => {
        return () => {
            if (unmountTimerRef.current)
                clearTimeout(unmountTimerRef.current);
        };
    }, []);
    // Close when clicking outside the input, popover, or toggle button.
    useEffect(() => {
        if (!open)
            return;
        const handler = (e) => {
            if (!popoverRef.current?.contains(e.target) &&
                !inputRef.current?.contains(e.target) &&
                !toggleButtonRef.current?.contains(e.target)) {
                closePopover();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open, closePopover]);
    // On mobile the popover may render below the fold; scroll it into view.
    useEffect(() => {
        if (open && popoverRef.current) {
            requestAnimationFrame(() => popoverRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
        }
    }, [open]);
    // When the parent changes `value` externally (e.g. form reset, linked
    // pickers), sync the text input and popover to match. When `value` becomes
    // `undefined` and the input isn't focused, reset the UI — but skip the
    // reset while the user is actively editing (the input is focused or the
    // popover is open) since `onChange(undefined)` fires during typing to
    // clear stale values and shouldn't wipe the user's in-progress input.
    useEffect(() => {
        if (!value) {
            const isUserEditing = document.activeElement === inputRef.current || openRef.current;
            if (!isUserEditing) {
                setInputValue('');
                setErrorMessage('');
                setViewDate(new Date());
                setViewMode('days');
            }
            return;
        }
        const formatted = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(value);
        setInputValue(formatted);
        setViewDate(new Date(value.getFullYear(), value.getMonth(), 1));
        setViewMode('days');
        const valueDay = startOfDay(value);
        if (minDate && valueDay < startOfDay(minDate))
            setErrorMessage(errors?.minDate || 'Date is before the earliest allowed date');
        else if (maxDate && valueDay > startOfDay(maxDate))
            setErrorMessage(errors?.maxDate || 'Date is after the latest allowed date');
        else
            setErrorMessage('');
    }, [value, locale, minDate, maxDate, errors]);
    // ── Helpers ───────────────────────────────────────────────────────────────
    const isDateInRange = useCallback((date) => {
        const d = startOfDay(date);
        return (!minDate || d >= startOfDay(minDate)) && (!maxDate || d <= startOfDay(maxDate));
    }, [minDate, maxDate]);
    /**
     * Called on every keystroke to keep the calendar in sync and commit the
     * value when the input becomes a complete, valid date.
     *
     * The split between "lenient parse for viewDate" and "strict parse for
     * onChange" means the calendar follows partial input (e.g. showing March
     * as soon as "03" is typed) without firing premature onChange callbacks.
     */
    const syncFromInput = useCallback((newInputValue, isFull) => {
        const lenientParsed = parseInputDate(newInputValue, format, true);
        if (lenientParsed)
            setViewDate(new Date(lenientParsed.getFullYear(), lenientParsed.getMonth(), 1));
        const parsed = parseInputDate(newInputValue, format);
        const error = validateInput(newInputValue, format, minDate, maxDate, true, errors);
        if (parsed && !error) {
            onChange?.(parsed);
            setErrorMessage('');
            if (openRef.current)
                closePopover();
        }
        else {
            if (value)
                onChange?.(undefined);
            if (error && (hasBlurred || isFull))
                setErrorMessage(error);
            else if (!error)
                setErrorMessage('');
        }
    }, [format, minDate, maxDate, errors, value, hasBlurred, onChange, closePopover]);
    // ── Handlers ──────────────────────────────────────────────────────────────
    /**
     * Keyboard handler for the text input.
     *
     * Escape / Enter control the popover. For digit keys, when the input is
     * already full (all digits present), we switch to overwrite mode: the
     * digit at the cursor is replaced rather than inserted, and the cursor
     * skips over separators. This lets users correct individual fields
     * without selecting text first.
     */
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            if (openRef.current)
                closePopover();
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            inputRef.current?.blur();
            if (openRef.current)
                closePopover();
            return;
        }
        if (e.key.length !== 1 || !/\d/.test(e.key) || e.ctrlKey || e.metaKey || e.altKey)
            return;
        const result = overwriteDigit(inputRef.current, e.key, format);
        if (!result)
            return;
        e.preventDefault();
        if (result.value === inputRef.current.value) {
            inputRef.current.setSelectionRange(result.cursorPos, result.cursorPos);
            return;
        }
        setInputValue(result.value);
        pendingCursorPos.current = { pos: result.cursorPos, forValue: result.value };
        syncFromInput(result.value, isInputFull(result.value, format));
    };
    /**
     * Change handler for the text input.
     *
     * Strips non-digit/non-separator characters, then either passes through
     * raw (on deletion) or runs auto-format (on insertion) to keep separators
     * in the right places.
     */
    const handleInputChange = (e) => {
        const rawValue = e.target.value;
        const rawCursor = e.target.selectionStart ?? rawValue.length;
        let filtered = '';
        let filteredCursor = 0;
        for (let i = 0; i < rawValue.length; i++) {
            const ch = rawValue[i];
            if (/\d/.test(ch) || SEPARATOR_CHARS.has(ch)) {
                filtered += ch;
                if (i < rawCursor)
                    filteredCursor++;
            }
        }
        const isDeletion = filtered.length < inputValue.length;
        const { value: finalValue, cursorPos: finalCursor } = isDeletion
            ? { value: filtered, cursorPos: filteredCursor }
            : autoFormat(filtered, filteredCursor, format);
        setInputValue(finalValue);
        pendingCursorPos.current = { pos: finalCursor, forValue: finalValue };
        syncFromInput(finalValue, isInputFull(finalValue, format));
    };
    /**
     * Blur handler performs final validation and reformats lenient input
     * (e.g. "2/2/26" → "02/02/2026").
     *
     * If focus moves into the popover (e.g. clicking a day button) we skip
     * validation to avoid flashing errors during the interaction.
     */
    const handleBlur = (e) => {
        if (popoverRef.current?.contains(e.relatedTarget))
            return;
        setHasBlurred(true);
        if (inputValue.trim() === '') {
            setErrorMessage('');
            return;
        }
        const parsed = parseInputDate(inputValue, format, true);
        const error = validateInput(inputValue, format, minDate, maxDate, true, errors);
        if (parsed && !error) {
            const formatted = new Intl.DateTimeFormat(locale, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }).format(parsed);
            setInputValue(formatted);
            const isSameDate = value &&
                parsed.getFullYear() === value.getFullYear() &&
                parsed.getMonth() === value.getMonth() &&
                parsed.getDate() === value.getDate();
            if (!isSameDate) {
                onChange?.(parsed);
                setViewDate(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
            }
            setErrorMessage('');
        }
        else if (error) {
            // Clear the parent's value so it doesn't hold a stale valid date
            // while the picker is showing an error — consistent with the
            // behaviour in syncFromInput during typing.
            onChange?.(undefined);
            setErrorMessage(error);
        }
    };
    const handleDateSelect = (date) => {
        if (!isDateInRange(date))
            return;
        onChange?.(date);
        setViewDate(new Date(date.getFullYear(), date.getMonth(), 1));
        closePopover();
        setViewMode('days');
        setErrorMessage('');
    };
    // ── Calendar views ────────────────────────────────────────────────────────
    //
    // The popover renders one of three drill-down views. The top row in each
    // view shows a label button that switches to the next level
    // (days → months → years) and prev/next navigation arrows.
    const CalendarTopRow = ({ onLabelClick, label: topLabel, ariaLabel, onPrev, onNext, }) => (jsxs("div", { className: styles.topRow, children: [jsxs("button", { onClick: onLabelClick, "aria-label": ariaLabel, className: styles.typeSelector, children: [jsx("span", { children: topLabel }), jsx(ChevronDown, { className: "h-3 w-3" })] }), jsxs("div", { className: styles.navContainer, children: [jsx("button", { onClick: onPrev, "aria-label": "Previous", className: styles.navButton, children: jsx(ChevronLeft, {}) }), jsx("button", { onClick: onNext, "aria-label": "Next", className: styles.navButton, children: jsx(ChevronRight, {}) })] })] }));
    const navigate = (unit, direction) => {
        let year = viewDate.getFullYear();
        let month = viewDate.getMonth();
        if (unit === 'month')
            month += direction;
        else
            year += direction;
        setViewDate(new Date(year, month, 1));
    };
    /** Day grid for a single month. Each button has a full accessible label. */
    const renderDaysView = () => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const firstDayOfWeek = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const today = new Date();
        const cells = [
            // Empty cells to align the 1st with the correct weekday column
            ...Array.from({ length: firstDayOfWeek }, (_, i) => (jsx("div", { className: styles.empty }, `e-${i}`))),
            ...Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const date = new Date(year, month, day);
                const isSelected = value &&
                    date.getDate() === value.getDate() &&
                    date.getMonth() === value.getMonth() &&
                    date.getFullYear() === value.getFullYear();
                const isDisabled = !isDateInRange(date);
                const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
                const cls = [
                    styles.baseButton,
                    styles.roundButton,
                    isSelected ? styles.selected : isToday ? styles.today : styles.transparent,
                    isDisabled && styles.disabled,
                ]
                    .filter(Boolean)
                    .join(' ');
                const fullLabel = date.toLocaleDateString(locale, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                });
                return (jsx("button", { onClick: () => handleDateSelect(date), disabled: isDisabled, "aria-label": fullLabel, "aria-selected": !!isSelected, "aria-current": isToday ? 'date' : undefined, className: cls, children: day }, day));
            }),
        ];
        return (jsxs("div", { className: styles.popoverContent, children: [jsx(CalendarTopRow, { onLabelClick: () => setViewMode('months'), label: jsxs(Fragment, { children: [viewDate.toLocaleDateString(locale, { month: 'short' }), " ", year] }), ariaLabel: "Switch to month view", onPrev: () => navigate('month', -1), onNext: () => navigate('month', 1) }), jsx("div", { className: styles.daysGrid, role: "row", children: weekDays.map((d, i) => (jsx("div", { className: styles.dayLetter, role: "columnheader", "aria-label": new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(new Date(2000, 0, i + 2)), children: d }, `${i}-${d}`))) }), jsx("div", { className: styles.daysGrid, role: "grid", "aria-label": viewDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' }), children: cells })] }));
    };
    /** 12-month grid for the currently viewed year. */
    const renderMonthsView = () => {
        return (jsxs("div", { className: styles.popoverContent, children: [jsx(CalendarTopRow, { onLabelClick: () => setViewMode('years'), label: viewDate.getFullYear(), ariaLabel: "Switch to year view", onPrev: () => navigate('year', -1), onNext: () => navigate('year', 1) }), jsx("div", { className: styles.monthYearGrid, children: monthNames.map((month, i) => {
                        const isSelected = value && i === value.getMonth() && viewDate.getFullYear() === value.getFullYear();
                        const isDisabled = !isMonthInRange(viewDate.getFullYear(), i, minDate, maxDate);
                        const cls = [
                            styles.baseButton,
                            styles.squareButton,
                            isSelected && styles.selected,
                            isDisabled && styles.disabled,
                        ]
                            .filter(Boolean)
                            .join(' ');
                        return (jsx("button", { onClick: () => {
                                setViewDate(new Date(viewDate.getFullYear(), i, 1));
                                setViewMode('days');
                            }, disabled: isDisabled, "aria-selected": !!isSelected, className: cls, children: month }, month));
                    }) })] }));
    };
    /** 12-year grid (e.g. 2024–2035). Clicking a year drills into the month view. */
    const renderYearsView = () => {
        const currentYear = viewDate.getFullYear();
        const startYear = Math.floor(currentYear / 12) * 12;
        const years = Array.from({ length: 12 }, (_, i) => startYear + i);
        return (jsxs("div", { className: styles.popoverContent, children: [jsx(CalendarTopRow, { onLabelClick: () => setViewMode('days'), label: jsxs(Fragment, { children: [startYear, " \u2013 ", startYear + 11] }), ariaLabel: "Switch to day view", onPrev: () => setViewDate(new Date(viewDate.getFullYear() - 12, viewDate.getMonth(), 1)), onNext: () => setViewDate(new Date(viewDate.getFullYear() + 12, viewDate.getMonth(), 1)) }), jsx("div", { className: styles.monthYearGrid, children: years.map((year) => {
                        const isSelected = value && year === value.getFullYear();
                        const inRange = (!minDate || new Date(year, 11, 31) >= startOfDay(minDate)) &&
                            (!maxDate || new Date(year, 0, 1) <= startOfDay(maxDate));
                        const cls = [
                            styles.baseButton,
                            styles.squareButton,
                            isSelected && styles.selected,
                            !inRange && styles.disabled,
                        ]
                            .filter(Boolean)
                            .join(' ');
                        return (jsx("button", { onClick: () => {
                                setViewDate(new Date(year, viewDate.getMonth(), 1));
                                setViewMode('months');
                            }, disabled: !inRange, "aria-selected": !!isSelected, className: cls, children: year }, year));
                    }) })] }));
    };
    // ── Render ────────────────────────────────────────────────────────────────
    const isValidDate = value && isDateInRange(value) && !errorMessage;
    return (jsxs("div", { className: "relative", children: [jsx(Input, { id: id, ref: inputRef, label: label, type: "text", inputMode: "numeric", value: inputValue, onChange: handleInputChange, onKeyDown: handleKeyDown, onBlur: handleBlur, onFocus: () => {
                    setHasBlurred(false);
                    if (!open)
                        openPopover();
                }, placeholder: format.placeholder, hasSuccess: !!isValidDate && !error, hasError: error || errorMessage, role: "combobox", "aria-expanded": open, "aria-haspopup": "dialog", "aria-controls": popoverId, "aria-autocomplete": "none", rightIcon: jsx("button", { ref: toggleButtonRef, onClick: () => (open ? closePopover() : openPopover()), "aria-label": open ? 'Close date picker' : 'Open date picker', className: `${styles.calendarButton}${isValidDate ? ' text-(--color-input-border-success)' : ''}`, children: jsx(CalendarIcon, {}) }) }), mounted && (jsxs("div", { id: popoverId, role: "dialog", "aria-modal": "false", "aria-label": "Date picker", ref: popoverRef, className: `${styles.popover} ${open ? 'scale-100' : 'scale-0'}`, onKeyDown: (e) => {
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        closePopover();
                        inputRef.current?.focus();
                    }
                }, children: [viewMode === 'days' && renderDaysView(), viewMode === 'months' && renderMonthsView(), viewMode === 'years' && renderYearsView()] }))] }));
}

export { DatePicker };
